import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Grid, MenuItem,
} from '@mui/material';
import FileUpload from '../../FileUpload';
import AttachmentList from '../../AttachmentList';

const calcModeConfig = {
  sqft: { label: 'Square Footage', priceLabel: 'Price per Sq Ft', unitLabel: 'Total Sq Ft' },
  lnft: { label: 'Linear Feet', priceLabel: 'Price per Linear Ft', unitLabel: 'Total Linear Ft' },
  sqyd: { label: 'Square Yards', priceLabel: 'Price per Sq Yard', unitLabel: 'Total Sq Yards' },
};

const ItemDialog = ({
  open, onClose, selectedItem, newItem, onNewItemChange,
  calcMode, calcData, onCalcModeChange, onCalcDataChange,
  onSubmit, projectRooms, isMobile,
  attachmentRefreshKey, onAttachmentUpdate, user,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    fullScreen={isMobile}
  >
    <DialogTitle>{selectedItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
    <DialogContent>
      <TextField
        select
        margin="dense"
        label="Room"
        fullWidth
        variant="outlined"
        value={newItem.room_id}
        onChange={(e) => onNewItemChange({ ...newItem, room_id: e.target.value })}
        sx={{ mt: 1, mb: 2 }}
      >
        {projectRooms?.map((room) => (
          <MenuItem key={room.id} value={room.id}>
            {room.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        margin="dense"
        label="Item Title"
        fullWidth
        variant="outlined"
        value={newItem.title}
        onChange={(e) => onNewItemChange({ ...newItem, title: e.target.value })}
        sx={{ mb: 2 }}
      />
      <TextField
        select
        margin="dense"
        label="Type"
        fullWidth
        variant="outlined"
        value={newItem.type}
        onChange={(e) => onNewItemChange({ ...newItem, type: e.target.value })}
        sx={{ mb: 2 }}
      >
        <MenuItem value="labor">Labor</MenuItem>
        <MenuItem value="materials">Materials</MenuItem>
        <MenuItem value="both">Materials and Labor</MenuItem>
      </TextField>
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          margin="dense"
          label="Pricing Method"
          fullWidth
          variant="outlined"
          value={calcMode}
          onChange={(e) => {
            onCalcModeChange(e.target.value);
          }}
          sx={{ mb: 2 }}
        >
          <MenuItem value="none">Enter Cost Directly</MenuItem>
          <MenuItem value="sqft">Calculate by Square Footage</MenuItem>
          <MenuItem value="lnft">Calculate by Linear Feet</MenuItem>
          <MenuItem value="sqyd">Calculate by Square Yards</MenuItem>
        </TextField>

        {calcMode !== 'none' && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label={calcModeConfig[calcMode].priceLabel}
                fullWidth
                variant="outlined"
                type="number"
                value={calcData.pricePerUnit}
                onChange={(e) => onCalcDataChange('pricePerUnit', e.target.value)}
                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label={calcModeConfig[calcMode].unitLabel}
                fullWidth
                variant="outlined"
                type="number"
                value={calcData.totalUnits}
                onChange={(e) => onCalcDataChange('totalUnits', e.target.value)}
              />
            </Grid>
          </Grid>
        )}

        <TextField
          margin="dense"
          label="Cost"
          fullWidth
          variant="outlined"
          type="number"
          value={newItem.cost}
          onChange={(e) => onNewItemChange({ ...newItem, cost: e.target.value })}
          disabled={calcMode !== 'none'}
          sx={{ mt: calcMode !== 'none' ? 2 : 0 }}
          InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
        />
      </Box>
      <TextField
        margin="dense"
        label="Image URL (Optional)"
        fullWidth
        variant="outlined"
        value={newItem.image_url}
        onChange={(e) => onNewItemChange({ ...newItem, image_url: e.target.value })}
        sx={{ mb: 2 }}
      />
      <TextField
        select
        margin="dense"
        label="Blocked by (Optional)"
        fullWidth
        variant="outlined"
        value={newItem.blocked_by_item_id || ''}
        onChange={(e) => onNewItemChange({ ...newItem, blocked_by_item_id: e.target.value || null })}
        helperText="Pick a prerequisite item — handy for things like 'tile after plumbing rough-in'"
        sx={{ mb: 2 }}
      >
        <MenuItem value=""><em>No prerequisite</em></MenuItem>
        {(projectRooms || []).flatMap((r) =>
          (r.items || [])
            .filter((it) => it.id !== selectedItem?.id)
            .map((it) => (
              <MenuItem key={it.id} value={it.id}>
                {r.name} — {it.title}
              </MenuItem>
            ))
        )}
      </TextField>
      <TextField
        margin="dense"
        label="Approval Deadline (Optional)"
        fullWidth
        variant="outlined"
        type="datetime-local"
        value={newItem.approval_deadline || ''}
        onChange={(e) => onNewItemChange({ ...newItem, approval_deadline: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />

      {selectedItem && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>File Attachments</Typography>
          <AttachmentList
            itemId={selectedItem.id}
            refreshKey={attachmentRefreshKey}
            onUpdate={onAttachmentUpdate}
          />
          <FileUpload
            itemId={selectedItem.id}
            userRole={user.role}
            onUploadSuccess={onAttachmentUpdate}
          />
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        disabled={!newItem.room_id || !newItem.title.trim() || !newItem.cost}
      >
        {selectedItem ? 'Save Changes' : 'Create'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ItemDialog;
